import { useEffect, useState, useRef } from 'react'

import { fetchAllStatuses, StatusData, ServiceStatus } from '../services/uptimeRobot'

function StatusIndicator({ status }: { status: ServiceStatus['status'] | StatusData['overall'] }) {
  let colorClass = 'bg-[#6b7280]' // unknown
  let label = 'Stato non disponibile'

  if (status === 'operational') {
    colorClass = 'bg-[#10b981]' // emerald
    label = 'Operativo'
  } else if (status === 'degraded') {
    colorClass = 'bg-[#f59e0b]' // amber
    label = 'Prestazioni ridotte'
  } else if (status === 'outage') {
    colorClass = 'bg-[#ef4444]' // red
    label = 'Non operativo'
  } else if (status === 'development') {
    colorClass = 'bg-[#3b82f6]' // blue
    label = 'In sviluppo'
  } else if (status === 'paused') {
    colorClass = 'bg-[#6b7280]' // gray
    label = 'In pausa'
  }

  return (
    <div className="flex items-center gap-2">
      <div className={`w-3 h-3 rounded-full ${colorClass} shadow-[0_0_8px_currentColor]`} />
      <span className="text-sm font-semibold uppercase tracking-wider">{label}</span>
    </div>
  )
}

export function StatusPage() {
  const [data, setData] = useState<StatusData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [rateLimited, setRateLimited] = useState(false)
  const [loading, setLoading] = useState(true)
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null)
  const lastFetchTime = useRef(0)

  const fetchStatus = async (isManual = false) => {
    const now = Date.now()
    if (isManual && now - lastFetchTime.current < 60000) {
      return // Cooldown di 60s sul pulsante Aggiorna
    }

    setLoading(true)
    setError(null)
    setRateLimited(false)
    try {
      const result = await fetchAllStatuses()
      setData(result)
      setLastUpdate(new Date(result.updatedAt))
      lastFetchTime.current = Date.now()
    } catch (e: any) {
      if (e.name === 'RateLimitError') {
        setRateLimited(true)
      } else {
        setError(e.message || 'Impossibile caricare lo stato dei servizi')
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    document.title = 'Stato Servizi | BlastCore'
    const link = document.createElement('link')
    link.rel = 'canonical'
    link.href = 'https://blastcore.it/status/'
    document.head.appendChild(link)

    const metaDescription = document.createElement('meta')
    metaDescription.name = 'description'
    metaDescription.content = 'Controlla lo stato in tempo reale dell\'infrastruttura di BlastCore: Sito Web, Java Server e Bedrock Server.'
    document.head.appendChild(metaDescription)

    const metaOgTitle = document.createElement('meta')
    metaOgTitle.setAttribute('property', 'og:title')
    metaOgTitle.content = 'Stato Servizi | BlastCore'
    document.head.appendChild(metaOgTitle)

    const metaOgUrl = document.createElement('meta')
    metaOgUrl.setAttribute('property', 'og:url')
    metaOgUrl.content = 'https://blastcore.it/status/'
    document.head.appendChild(metaOgUrl)

    fetchStatus()
    const interval = setInterval(() => fetchStatus(), 300000) // 300 secondi (5 min)

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now()
        // Se sono passati più di 5 minuti, aggiorna
        if (now - lastFetchTime.current > 300000) {
          fetchStatus()
        }
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      document.head.removeChild(link)
      document.head.removeChild(metaDescription)
      document.head.removeChild(metaOgTitle)
      document.head.removeChild(metaOgUrl)
    }
  }, [])

  return (
    <main className="section page min-h-screen pt-32 pb-16 px-4 md:px-8 max-w-4xl mx-auto">
      <header className="mb-12 text-center">
        <h1 className="mb-4 text-4xl md:text-5xl font-bold">Stato Servizi</h1>
        <p className="text-[var(--muted)]">Controlla lo stato in tempo reale della nostra infrastruttura.</p>
      </header>

      {error && !data && (
        <div className="p-6 mb-8 border border-[var(--error)] bg-[var(--error)]/10 rounded-[var(--cut)]">
          <p className="text-[var(--error)] font-semibold">{error}</p>
          <button onClick={() => fetchStatus(true)} className="mt-4 button button--secondary button--compact">
            Riprova
          </button>
        </div>
      )}

      {loading && !data && (
        <div className="flex justify-center items-center py-20">
          <div className="w-10 h-10 border-4 border-[var(--primary)] border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      {data && (
        <div className="flex flex-col gap-8">
          <section className="p-6 md:p-8 bg-[var(--surface-low)] border border-[var(--surface-high)] rounded-[var(--cut)] flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h2 className="text-xl text-[var(--muted)] mb-2 uppercase font-mono tracking-widest">Stato Generale</h2>
              <StatusIndicator status={data.overall} />
            </div>
            
            <div className="text-right flex flex-col md:items-end gap-3">
              <div className="text-sm text-[var(--muted)]">
                Ultimo aggiornamento: <br className="md:hidden" />
                <span className="text-white font-mono">
                  {lastUpdate ? lastUpdate.toLocaleTimeString() : '--:--'}
                </span>
                {data.stale && <span className="ml-2 text-[var(--error)]">(Dati non aggiornati)</span>}
                {rateLimited && <span className="ml-2 text-amber-500 font-semibold">(Aggiornamento limitato)</span>}
              </div>
            </div>
          </section>

          <section className="flex flex-col gap-4">
            <h3 className="text-lg uppercase font-mono tracking-widest text-[var(--muted)] mb-2">Servizi</h3>
            {data.services.map(service => (
              <div 
                key={service.id} 
                className="p-5 md:p-6 bg-[var(--surface-low)] border border-[var(--surface-high)] rounded-[var(--cut)] transition-transform hover:translate-y-[-2px] flex flex-col gap-4"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h4 className="text-xl font-bold font-sans tracking-wide mb-1 text-white">{service.name}</h4>
                    <StatusIndicator status={service.status} />
                  </div>
                  
                  <div className="flex gap-6 text-sm text-[var(--muted)]">
                    {service.uptime && (
                      <div className="flex flex-col">
                        <span className="uppercase text-[10px] tracking-widest mb-1">Uptime (30gg)</span>
                        <span className="font-mono text-white text-lg">{service.uptime.month}%</span>
                      </div>
                    )}
                    {service.responseTimeMs !== null && (
                      <div className="flex flex-col">
                        <span className="uppercase text-[10px] tracking-widest mb-1">Latenza</span>
                        <span className="font-mono text-white text-lg">{service.responseTimeMs}ms</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </section>
        </div>
      )}
    </main>
  )
}
